import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'

import { GasSettingsModal } from '../gas-settings-modal/gas-settings-modal'

import { TransferFeeEditButton } from './transfer-fee-edit-button'

import type { TransferGasSettingsReatom } from '../../../atoms/transfer-fee-reatom'
import type { ClassNameProps } from '@root/shared/react'

type TransferFeeEvmProps = {
  model: TransferGasSettingsReatom
} & ClassNameProps

export const TransferFeeEvm: React.FC<TransferFeeEvmProps> = ({
  model,
  className,
}) => {
  const { t } = useTranslation()

  const [settingsSelection] = useAtom(model.selectedGasSettings)
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const [estimatedTransferFeeState] = useAtom(
    model.estimatedTransferGasFeeEthStateAtom,
  )
  const [estimatedTransferMaxGasFeeEthState] = useAtom(
    model.estimatedTransferMaxGasFeeEthStateAtom,
  )
  const [estimatedGasFeeUsd] = useAtom(model.estimatedGasFeeUsdAtom)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)

  const renderEstimatedFee = () => {
    if (!AsyncStateN.isSuccess(estimatedTransferFeeState)) {
      return <Skeleton height={16} width={60} paddingBlock={2} />
    }

    return (
      <FeeValue>
        <Numeric
          value={estimatedTransferFeeState.value}
          preset="currency-amount-short"
          currency={nativeCurrency.ticker}
        />
      </FeeValue>
    )
  }

  const renderMaxFee = () => {
    if (!AsyncStateN.isSuccess(estimatedTransferMaxGasFeeEthState)) {
      return <Skeleton height={12} width={50} />
    }

    return (
      <MaxFeeValue>
        <Numeric
          value={estimatedTransferMaxGasFeeEthState.value}
          preset="currency-amount-short"
          currency={nativeCurrency.ticker}
        />
      </MaxFeeValue>
    )
  }

  return (
    <Root className={className}>
      <MainContent>
        <EstimatedFeeContainer>
          <FeeLabel>{t('gas-settings.estimated-fee')}:</FeeLabel>
          {renderEstimatedFee()}
        </EstimatedFeeContainer>

        <MaxFee>
          <MaxFeeLabel>{t('gas-settings.max-fee')}:</MaxFeeLabel>
          {renderMaxFee()}
        </MaxFee>
      </MainContent>
      <TransferFeeEditButton
        settingsSelection={settingsSelection}
        estimatedGasFeeUsd={estimatedGasFeeUsd}
        onEdit={() => {
          setSettingsModalOpen(true)
        }}
      />
      <GasSettingsModal
        open={settingsModalOpen}
        onOpenChange={setSettingsModalOpen}
        gasSettingsModel={model}
      />
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const EstimatedFeeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  color: var(--text-11);

  font-size: 12px;
  font-weight: 500;
`
const FeeLabel = styled.div``

const FeeValue = styled.div``

const MainContent = styled.div`
  display: grid;
  align-items: center;
  grid-template-rows: auto auto;
  gap: 2px;
  padding-inline: 8px;
`

const MaxFee = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: start;
  justify-items: start;
  gap: 2px;
  color: var(--text-8);
  font-size: 11px;
  font-weight: 500;
`

const MaxFeeLabel = styled.div``

const MaxFeeValue = styled.div``
